import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSelect } from '../components/LanguageSelect';
import {
  getJSON,
  postJSON,
  type TransferNetworkResponse,
  type TransferPendingNonceResponse,
  type TransferPrepareResponse,
  type TransferSubmitResponse,
  type UserProfile,
  type WalletDetail,
  type WatchedTokenBalancesResponse,
  type WatchedTokenRow,
  userWalletApiBase,
} from '../lib/api';
import { explorerAddressUrl, explorerTxUrl } from '../lib/explorers';
import { prefillEip1559Gwei } from '../lib/gasFees';
import { useWalletSessionUnlocked } from '../hooks/useWalletSessionUnlocked';
import { isWalletSessionUnlocked } from '../lib/mpcUnlockCache';
import { signDigestWithWalletMpc } from '../lib/mpcSession';
import { fetchTokenUsdPrice, formatUsdPrice } from '../lib/tokenPrice';
import { DEFAULT_TOKEN_SPENDER, WRAPPED_NATIVE_BY_CHAIN, isWrappedNativeToken } from '../lib/wrappedNative';
import { appMainClass, pageWrapClass } from '../layout/page';
import {
  inputFieldClass,
  inputMonoFieldClass,
  inputMonoStackFieldClass,
  labelClass,
  outlineSoftButtonClass,
  primaryButtonBlockClass,
  successButtonBlockClass,
  warningButtonBlockClass,
} from '../components/form/fieldClasses';

function parseNonceInput(raw: string): number | undefined {
  const t = raw.trim();
  if (!t) {
    return undefined;
  }
  if (!/^\d+$/.test(t)) {
    throw new Error('INVALID_NONCE');
  }
  const n = Number(t);
  if (!Number.isSafeInteger(n) || n < 0) {
    throw new Error('INVALID_NONCE');
  }
  return n;
}

export default function TokenDetailPage() {
  const { id, chain: chainParam, tokenRef: tokenRefParam } = useParams<{
    id: string;
    chain: string;
    tokenRef: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const walletId = Number(id);
  const payUnlocked = useWalletSessionUnlocked(walletId);
  const chain = decodeURIComponent(chainParam ?? '').trim();
  const tokenRef = decodeURIComponent(tokenRefParam ?? '').trim();

  const st = location.state as { row?: WatchedTokenRow } | null;
  const [symbol, setSymbol] = useState(st?.row?.symbol ?? '');
  const [name, setName] = useState(st?.row?.name ?? '');
  const [decimals, setDecimals] = useState(st?.row?.decimals ?? 18);
  const [balanceLine, setBalanceLine] = useState('');
  const [usdPrice, setUsdPrice] = useState<number | null>(null);
  const [usdPriceLoading, setUsdPriceLoading] = useState(false);

  const [wallet, setWallet] = useState<WalletDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [msgOk, setMsgOk] = useState(false);

  const [allowanceFormatted, setAllowanceFormatted] = useState('—');
  const [allowanceLoading, setAllowanceLoading] = useState(false);
  const [spender, setSpender] = useState(DEFAULT_TOKEN_SPENDER);

  const [approveAmount, setApproveAmount] = useState('');
  const [approveMax, setApproveMax] = useState(false);
  const [wrapAmount, setWrapAmount] = useState('');
  const [unwrapAmount, setUnwrapAmount] = useState('');

  const [maxFeeGwei, setMaxFeeGwei] = useState('2');
  const [priorityFeeGwei, setPriorityFeeGwei] = useState('1');
  const [paymentPassword, setPaymentPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [profile, setProfile] = useState<Pick<UserProfile, 'mfa_enabled'> | null>(null);
  const [nonceInput, setNonceInput] = useState('');
  const [nonceLoadErr, setNonceLoadErr] = useState('');
  const [baseFeeLine, setBaseFeeLine] = useState('');
  const [busy, setBusy] = useState<'approve' | 'wrap' | 'unwrap' | null>(null);
  const [lastTxHash, setLastTxHash] = useState('');

  const nonceReqGen = useRef(0);
  const nonceDirtyRef = useRef(false);
  const balGen = useRef(0);
  const priceGen = useRef(0);

  const base = Number.isFinite(walletId) && walletId > 0 ? userWalletApiBase(walletId) : '';
  const isNative = tokenRef === 'native';
  const wrappedHex = WRAPPED_NATIVE_BY_CHAIN[chain];
  const showWrap = isNative && !!wrappedHex;
  const showUnwrap = !isNative && isWrappedNativeToken(chain, tokenRef);
  const showErc20Extras = !isNative;

  const addrDisplay = isNative ? '—' : `0x${tokenRef}`;
  const addrHref = !isNative ? explorerAddressUrl(chain, `0x${tokenRef}`) : null;

  const loadWallet = useCallback(async () => {
    if (!base) {
      return;
    }
    const [wr, pr] = await Promise.all([
      getJSON<WalletDetail>(base),
      getJSON<UserProfile>('/api/user/profile'),
    ]);
    if (wr.res.status === 401 || pr.res.status === 401) {
      navigate('/', { replace: true });
      return;
    }
    if (wr.res.status === 403 || pr.res.status === 403) {
      navigate('/home', { replace: true });
      return;
    }
    if (pr.res.ok) {
      setProfile({ mfa_enabled: pr.data.mfa_enabled });
    } else {
      setProfile(null);
    }
    if (!wr.res.ok) {
      setWallet(null);
      setMsg(wr.data.error || t('common.error'));
      return;
    }
    setWallet(wr.data);
  }, [base, navigate, t]);

  useEffect(() => {
    let c = false;
    void (async () => {
      setLoading(true);
      setMsg('');
      if (!Number.isFinite(walletId) || walletId <= 0 || !chain || !tokenRef) {
        setLoading(false);
        return;
      }
      await loadWallet();
      if (c) {
        return;
      }
      setLoading(false);
    })();
    return () => {
      c = true;
    };
  }, [walletId, chain, tokenRef, loadWallet]);

  useEffect(() => {
    nonceDirtyRef.current = false;
  }, [walletId, chain]);

  useEffect(() => {
    const gen = ++nonceReqGen.current;
    setNonceLoadErr('');
    if (!walletId || !chain) {
      setNonceInput('');
      return;
    }
    void (async () => {
      const { res, data } = await postJSON<TransferPendingNonceResponse>('/api/user/transfer/pending-nonce', {
        wallet_id: walletId,
        chain,
      });
      if (gen !== nonceReqGen.current) {
        return;
      }
      if (!res.ok) {
        setNonceLoadErr(data.error || t('transfer.nonceLoadError'));
        return;
      }
      if (nonceDirtyRef.current) {
        return;
      }
      if (typeof data.nonce === 'number' && Number.isFinite(data.nonce)) {
        setNonceInput(String(data.nonce));
      }
    })();
  }, [walletId, chain, t]);

  useEffect(() => {
    const gen = ++balGen.current;
    if (!walletId || !chain || !tokenRef || !base) {
      setBalanceLine('');
      return;
    }
    void (async () => {
      const { res, data } = await postJSON<WatchedTokenBalancesResponse>(`${base}/tokens/balances`, {
        tokens: [{ chain, token_ref: tokenRef }],
      });
      if (gen !== balGen.current) {
        return;
      }
      if (!res.ok) {
        setBalanceLine(data.error || t('common.error'));
        return;
      }
      const row = data.balances?.[0];
      if (row?.symbol) {
        setSymbol(row.symbol);
      }
      if (row?.name) {
        setName(row.name);
      }
      if (typeof row?.decimals === 'number') {
        setDecimals(row.decimals);
      }
      if (row?.balance_error) {
        setBalanceLine(row.balance_error);
        return;
      }
      const sym = row?.symbol?.trim() || symbol;
      const amt = row?.balance_formatted?.trim() ?? '0';
      setBalanceLine(sym ? `${amt} ${sym}` : amt);
    })();
  }, [walletId, chain, tokenRef, base, t, symbol]);

  useEffect(() => {
    const gen = ++priceGen.current;
    setUsdPrice(null);
    if (!chain || !tokenRef) {
      setUsdPriceLoading(false);
      return;
    }
    setUsdPriceLoading(true);
    void (async () => {
      try {
        const p = await fetchTokenUsdPrice(chain, tokenRef);
        if (gen !== priceGen.current) {
          return;
        }
        setUsdPrice(p);
      } catch {
        if (gen !== priceGen.current) {
          return;
        }
        setUsdPrice(null);
      } finally {
        if (gen === priceGen.current) {
          setUsdPriceLoading(false);
        }
      }
    })();
  }, [chain, tokenRef]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { res, data } = await postJSON<TransferNetworkResponse>('/api/user/transfer/network', { chain });
      if (cancelled) {
        return;
      }
      if (!res.ok) {
        setBaseFeeLine(data.error || '');
        return;
      }
      const suggested = prefillEip1559Gwei(data.gas_price_gwei, data.base_fee_gwei);
      if (suggested) {
        setMaxFeeGwei(suggested.maxFeeGwei);
        setPriorityFeeGwei(suggested.priorityFeeGwei);
      }
      const baseG = data.base_fee_gwei?.trim() || '0';
      setBaseFeeLine(t('transfer.baseFeeLine', { gwei: baseG }));
    })();
    return () => {
      cancelled = true;
    };
  }, [chain, t]);

  const refreshAllowance = useCallback(async () => {
    if (!base || isNative) {
      return;
    }
    setAllowanceLoading(true);
    try {
      const { res, data } = await postJSON<{
        allowance_formatted?: string;
        error?: string;
      }>(`${base}/token/allowance`, {
        chain,
        token_ref: tokenRef,
        spender: spender.trim() || DEFAULT_TOKEN_SPENDER,
      });
      if (!res.ok) {
        setAllowanceFormatted(data.error || '—');
        return;
      }
      setAllowanceFormatted(data.allowance_formatted ?? '0');
    } finally {
      setAllowanceLoading(false);
    }
  }, [base, chain, tokenRef, isNative, spender]);

  useEffect(() => {
    if (!base || isNative) {
      return;
    }
    let cancelled = false;
    void (async () => {
      setAllowanceLoading(true);
      const { res, data } = await postJSON<{
        allowance_formatted?: string;
        error?: string;
      }>(`${base}/token/allowance`, {
        chain,
        token_ref: tokenRef,
        spender: DEFAULT_TOKEN_SPENDER,
      });
      if (cancelled) {
        return;
      }
      if (!res.ok) {
        setAllowanceFormatted(data.error || '—');
      } else {
        setAllowanceFormatted(data.allowance_formatted ?? '0');
      }
      setAllowanceLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [base, chain, tokenRef, isNative]);

  async function mpcSignSubmit(
    preparePath: string,
    submitPath: string,
    prepareBody: Record<string, unknown>,
    submitMerge: Record<string, unknown>,
  ): Promise<string> {
    const payPwd = paymentPassword.trim();
    if (!payPwd && !payUnlocked && !(await isWalletSessionUnlocked(walletId))) {
      throw new Error(t('transfer.paymentPasswordRequired'));
    }
    let nonceExplicit: number | undefined;
    try {
      nonceExplicit = parseNonceInput(nonceInput);
    } catch {
      throw new Error(t('transfer.nonceInvalid'));
    }
    const maxFee = maxFeeGwei.trim();
    const priorityFee = priorityFeeGwei.trim();
    prepareBody.max_fee_gwei = maxFee;
    prepareBody.priority_fee_gwei = priorityFee;
    if (nonceExplicit !== undefined) {
      prepareBody.nonce = nonceExplicit;
    }
    const { res: prepRes, data: prep } = await postJSON<TransferPrepareResponse>(preparePath, prepareBody);
    if (!prepRes.ok) {
      throw new Error(prep.error || t('common.error'));
    }
    const signOut = await signDigestWithWalletMpc(walletId, prep.digest_hex!, payPwd, {
      commonErrorMessage: t('common.error'),
      sessionMissingMessage: t('transfer.sessionMissing'),
      sessionInvalidMessage: t('transfer.sessionInvalid'),
      mfaCode: profile?.mfa_enabled ? mfaCode.trim() : undefined,
    });
    const subBody = {
      ...submitMerge,
      wallet_id: walletId,
      chain,
      max_fee_gwei: maxFee,
      priority_fee_gwei: priorityFee,
      nonce: prep.nonce,
      gas_limit: prep.gas_limit,
      r_hex: signOut.r_hex,
      s_hex: signOut.s_hex,
      v: signOut.v,
    };
    const { res: subRes, data: subData } = await postJSON<TransferSubmitResponse>(submitPath, subBody);
    if (!subRes.ok) {
      throw new Error(subData.error || t('common.error'));
    }
    setMfaCode('');
    return subData.tx_hash ?? '';
  }

  async function onApprove(e: FormEvent) {
    e.preventDefault();
    if (!base) {
      return;
    }
    setBusy('approve');
    setMsg('');
    try {
      const amt = approveMax ? 'max' : approveAmount.trim();
      if (!approveMax && !amt) {
        throw new Error(t('tokenDetail.approveAmountRequired'));
      }
      const hash = await mpcSignSubmit(
        `${base}/token/approve/prepare`,
        `${base}/token/approve/submit`,
        {
          wallet_id: walletId,
          chain,
          token_ref: tokenRef,
          spender: spender.trim() || DEFAULT_TOKEN_SPENDER,
          amount: amt,
        },
        {
          token_ref: tokenRef,
          spender: spender.trim() || DEFAULT_TOKEN_SPENDER,
          amount: amt,
        },
      );
      setMsgOk(true);
      setMsg(t('tokenDetail.approveOk'));
      setLastTxHash(hash);
      await refreshAllowance();
    } catch (err) {
      setMsgOk(false);
      setMsg(String(err));
    } finally {
      setBusy(null);
    }
  }

  async function onWrap(e: FormEvent) {
    e.preventDefault();
    if (!base) {
      return;
    }
    setBusy('wrap');
    setMsg('');
    try {
      const hash = await mpcSignSubmit(
        `${base}/token/wrap/prepare`,
        `${base}/token/wrap/submit`,
        {
          wallet_id: walletId,
          chain,
          amount: wrapAmount.trim(),
        },
        { amount: wrapAmount.trim() },
      );
      setMsgOk(true);
      setMsg(t('tokenDetail.wrapOk'));
      setLastTxHash(hash);
    } catch (err) {
      setMsgOk(false);
      setMsg(String(err));
    } finally {
      setBusy(null);
    }
  }

  async function onUnwrap(e: FormEvent) {
    e.preventDefault();
    if (!base) {
      return;
    }
    setBusy('unwrap');
    setMsg('');
    try {
      const hash = await mpcSignSubmit(
        `${base}/token/unwrap/prepare`,
        `${base}/token/unwrap/submit`,
        {
          wallet_id: walletId,
          chain,
          token_ref: tokenRef,
          amount: unwrapAmount.trim(),
        },
        {
          token_ref: tokenRef,
          amount: unwrapAmount.trim(),
        },
      );
      setMsgOk(true);
      setMsg(t('tokenDetail.unwrapOk'));
      setLastTxHash(hash);
    } catch (err) {
      setMsgOk(false);
      setMsg(String(err));
    } finally {
      setBusy(null);
    }
  }

  const titleSymbol = useMemo(() => symbol || (isNative ? t('tokenDetail.nativeLabel') : tokenRef.slice(0, 8)), [
    symbol,
    isNative,
    tokenRef,
    t,
  ]);

  function feeNonceBlock() {
    return (
      <>
        {baseFeeLine ? <p className="text-xs text-slate-500">{baseFeeLine}</p> : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className={labelClass}>{t('transfer.maxFee')}</label>
            <input className={inputMonoFieldClass} value={maxFeeGwei} onChange={(e) => setMaxFeeGwei(e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>{t('transfer.priorityFee')}</label>
            <input
              className={inputMonoFieldClass}
              value={priorityFeeGwei}
              onChange={(e) => setPriorityFeeGwei(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className={labelClass}>{t('transfer.nonce')}</label>
          <input
            className={inputMonoFieldClass}
            value={nonceInput}
            onChange={(e) => {
              nonceDirtyRef.current = true;
              setNonceInput(e.target.value);
            }}
          />
          {nonceLoadErr ? <p className="mt-1 text-xs text-amber-800">{nonceLoadErr}</p> : null}
        </div>
      </>
    );
  }

  function paymentBlock() {
    return (
      <>
        <div>
          <label className={labelClass}>{t('transfer.paymentPassword')}</label>
          <input
            type="password"
            required={!payUnlocked}
            autoComplete="current-password"
            className={inputFieldClass}
            value={paymentPassword}
            onChange={(e) => setPaymentPassword(e.target.value)}
            placeholder={payUnlocked ? t('transfer.payPwdUnlockHint') : undefined}
          />
          {payUnlocked ? (
            <p className="mt-1 text-xs text-slate-500">{t('transfer.payPwdUnlockHint')}</p>
          ) : null}
        </div>
        {profile?.mfa_enabled ? (
          <div>
            <label className={labelClass}>{t('mfa.code')}</label>
            <input
              required
              inputMode="numeric"
              autoComplete="one-time-code"
              className={inputFieldClass}
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
            />
          </div>
        ) : null}
      </>
    );
  }

  if (!Number.isFinite(walletId) || walletId <= 0 || !chain || !tokenRef) {
    return (
      <div className={pageWrapClass}>
        <p className="text-sm text-red-600">{t('tokenDetail.badRoute')}</p>
        <Link to="/home" className="mt-4 inline-block text-blue-600">
          {t('wallet.backHome')}
        </Link>
      </div>
    );
  }

  if (loading || !wallet) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-slate-100 text-slate-600">
        {loading ? t('home.loading') : msg || t('common.error')}
      </div>
    );
  }

  return (
    <div className={pageWrapClass}>
      <main className={appMainClass}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link to={`/home/wallet/${walletId}`} className="text-sm font-medium text-blue-600 hover:underline">
            ← {t('tokenDetail.backWallet')}
          </Link>
          <LanguageSelect />
        </div>

        <h1 className="text-2xl font-semibold text-slate-900">
          {titleSymbol}
          <span className="text-lg font-normal text-slate-500"> · {t(`wallet.chain.${chain}`)}</span>
        </h1>

        {msg ? (
          <div
            role="status"
            className={`rounded-xl border px-4 py-3 text-sm font-medium ${
              msgOk ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-red-200 bg-red-50 text-red-800'
            }`}
          >
            {msg}
          </div>
        ) : null}

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">{t('tokenDetail.metaTitle')}</h2>
          <dl className="mt-3 grid gap-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">{t('tokenDetail.name')}</dt>
              <dd className="text-right text-slate-900">{name || '—'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">{t('tokenDetail.symbol')}</dt>
              <dd className="font-mono text-slate-900">{symbol || '—'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">{t('tokenDetail.decimals')}</dt>
              <dd className="font-mono text-slate-900">{decimals}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">{t('tokenDetail.contract')}</dt>
              <dd className="min-w-0 break-all text-right">
                {addrHref ? (
                  <a href={addrHref} target="_blank" rel="noopener noreferrer" className="font-mono text-blue-700 underline">
                    {addrDisplay}
                  </a>
                ) : (
                  <span className="text-slate-700">{t('tokenDetail.nativeNoContract')}</span>
                )}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">{t('tokenDetail.usdPrice')}</dt>
              <dd className="text-right font-mono text-sm text-slate-800">
                {usdPriceLoading
                  ? t('tokenDetail.usdPriceLoading')
                  : usdPrice != null
                    ? `$${formatUsdPrice(usdPrice)}`
                    : '—'}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">{t('tokenDetail.balance')}</dt>
              <dd className="break-all text-right font-mono text-sm text-slate-800">{balanceLine || '—'}</dd>
            </div>
          </dl>
        </section>

        {showErc20Extras ? (
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">{t('tokenDetail.allowanceTitle')}</h2>
            <p className="mt-1 text-sm text-slate-600">{t('tokenDetail.allowanceHint')}</p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className="text-sm text-slate-700">
                {allowanceLoading ? t('wallet.balanceLoading') : allowanceFormatted}
              </span>
              <button type="button" className={outlineSoftButtonClass} onClick={() => void refreshAllowance()}>
                {t('tokenDetail.refreshAllowance')}
              </button>
            </div>
            <label className={`mt-4 ${labelClass}`} htmlFor="spender">
              {t('tokenDetail.spender')}
            </label>
            <input
              id="spender"
              className={inputMonoFieldClass}
              value={spender}
              onChange={(e) => setSpender(e.target.value)}
            />
          </section>
        ) : null}

        {showErc20Extras ? (
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">{t('tokenDetail.approveTitle')}</h2>
            <form className="mt-4 space-y-3" onSubmit={(e) => void onApprove(e)}>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={approveMax} onChange={(e) => setApproveMax(e.target.checked)} />
                {t('tokenDetail.approveUnlimited')}
              </label>
              {!approveMax ? (
                <input
                  className={inputMonoStackFieldClass}
                  placeholder={t('tokenDetail.approveAmountPh')}
                  value={approveAmount}
                  onChange={(e) => setApproveAmount(e.target.value)}
                />
              ) : null}
              {feeNonceBlock()}
              {paymentBlock()}
              <button type="submit" disabled={busy !== null} className={primaryButtonBlockClass}>
                {busy === 'approve' ? t('tokenDetail.submitting') : t('tokenDetail.approveSubmit')}
              </button>
            </form>
          </section>
        ) : null}

        {showWrap ? (
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">{t('tokenDetail.wrapTitle')}</h2>
            <p className="mt-1 text-sm text-slate-600">{t('tokenDetail.wrapHint')}</p>
            <form className="mt-4 space-y-3" onSubmit={(e) => void onWrap(e)}>
              <input
                required
                className={inputMonoStackFieldClass}
                placeholder={t('tokenDetail.wrapAmountPh')}
                value={wrapAmount}
                onChange={(e) => setWrapAmount(e.target.value)}
              />
              {feeNonceBlock()}
              {paymentBlock()}
              <button type="submit" disabled={busy !== null} className={successButtonBlockClass}>
                {busy === 'wrap' ? t('tokenDetail.submitting') : t('tokenDetail.wrapSubmit')}
              </button>
            </form>
          </section>
        ) : null}

        {showUnwrap ? (
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">{t('tokenDetail.unwrapTitle')}</h2>
            <p className="mt-1 text-sm text-slate-600">{t('tokenDetail.unwrapHint')}</p>
            <form className="mt-4 space-y-3" onSubmit={(e) => void onUnwrap(e)}>
              <input
                required
                className={inputMonoStackFieldClass}
                placeholder={t('tokenDetail.unwrapAmountPh')}
                value={unwrapAmount}
                onChange={(e) => setUnwrapAmount(e.target.value)}
              />
              {feeNonceBlock()}
              {paymentBlock()}
              <button type="submit" disabled={busy !== null} className={warningButtonBlockClass}>
                {busy === 'unwrap' ? t('tokenDetail.submitting') : t('tokenDetail.unwrapSubmit')}
              </button>
            </form>
          </section>
        ) : null}

        {lastTxHash ? (
          <p className="break-all text-sm text-slate-700">
            {t('transfer.txHash')}:{' '}
            {explorerTxUrl(chain, lastTxHash) ? (
              <a href={explorerTxUrl(chain, lastTxHash)!} className="font-mono text-blue-700 underline" target="_blank" rel="noopener noreferrer">
                {lastTxHash}
              </a>
            ) : (
              <span className="font-mono">{lastTxHash}</span>
            )}
          </p>
        ) : null}
      </main>
    </div>
  );
}
